import type { ReactNode } from 'react'
import { ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react'
import { tone as toneOf, type Tone } from '@/lib/format'

/* Delta — colored directional value. */
export function Delta({
  children, value, higherIsBetter = true, forceTone,
}: { children: ReactNode; value?: number; higherIsBetter?: boolean; forceTone?: Tone }) {
  const t: Tone = forceTone ?? toneOf(value, higherIsBetter)
  const Icon = t === 'up' ? ArrowUpRight : t === 'down' ? ArrowDownRight : Minus
  return (
    <span className={`delta delta--${t}`}>
      <Icon size={13} strokeWidth={2.4} />
      {children}
    </span>
  )
}

/* KPI tile — label, big serif value, optional unit + delta. */
export function KpiTile({
  label, value, unit, delta, accent = false, sub, onClick,
}: {
  label: ReactNode; value: ReactNode; unit?: ReactNode
  delta?: ReactNode; accent?: boolean; sub?: ReactNode
  /** Makes the tile a navigation target (pointer + hover affordance). */
  onClick?: () => void
}) {
  return (
    <div
      className={`kpi${accent ? ' kpi--accent' : ''}${onClick ? ' kpi--click' : ''}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick() } } : undefined}
    >
      <div className="kpi-label label">{label}</div>
      <div className="kpi-value-row">
        <span className="kpi-value num">{value}</span>
        {unit && <span className="kpi-unit">{unit}</span>}
      </div>
      {(delta || sub) && <div className="kpi-foot">{delta}{sub && <span className="kpi-sub">{sub}</span>}</div>}
    </div>
  )
}

export function KpiGrid({ cols = 4, children }: { cols?: 2 | 3 | 4 | 5; children: ReactNode }) {
  return <div className={`kpi-grid kpi-grid--${cols}`}>{children}</div>
}
